/* eslint-disable react/jsx-props-no-spreading,react/prop-types */
import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import asyncComponent from '../util/asyncComponent';
import { useSelector } from 'react-redux';
import { getUserAccess } from 'modules/cbre-flow-api-ducks/selectors';
import { AccessDenied } from 'components/AccessDenied';

// Define all async components outside the Routes component
const AsyncDashboard = asyncComponent(() => import('./Dashboard'));
const AsyncLabInstrument = asyncComponent(() => import('./LabInstrument'));
const AsyncLabInstrumentDetails = asyncComponent(() => import('./LabInstrument/labInstrumentDetails'));
const AsyncRequests = asyncComponent(() => import('./Requests'));
const AsyncRequestDetails = asyncComponent(() => import('./Requests/requestDetails'));
const AsyncResizableDashboard = asyncComponent(() => import('./ResizableDashboard'));
const AsyncSiteSelection = asyncComponent(() => import('./SiteSelection'));
const AsyncTableauReports = asyncComponent(() => import('../modules/cbre-flow-tableau-reports/components/TableauReports'));
const AsyncPDFGeneration = asyncComponent(() => import('../modules/cbre-flow-pdf-generation/components/PDFGeneration'));
const AsyncReviewWindowDetails = asyncComponent(() => import('../modules/cbre-flow-lab-entitlement-review-window-details').then((module) => ({ default: module.ReviewWindowDetails })));
const AsyncApprovalsContainer = asyncComponent(() => import('modules/cbre-flow-approvals').then((module) => ({ default: module.ApprovalsContainer })));
const AsyncRootEntitlementsReview = asyncComponent(() => import('modules/cbre-flow-entitlements-review').then((module) => ({ default: module.RootEntitlementsReview })));
const AsyncReviewerWindowDetails = asyncComponent(() => import('modules/cbre-flow-lab-entitlement-reviewer-window-details').then((module) => ({ default: module.ReviewerWindowDetails })));
const AsyncRootEntitlementsReviewerWindow = asyncComponent(() => import('modules/cbre-flow-entitlements-reviewer-window').then((module) => ({ default: module.RootEntitlementsReviewerWindow })));
const AsyncRootLabDashboard = asyncComponent(() => import('../modules/cbre-flow-lab-dashboard/components/rootLabDashboard'));
const AsyncUserRequests = asyncComponent(() => import('./Administration/UserRequests'));
const PendingSiteAccess = asyncComponent(() => import('../modules/cbre-pending-site-access/index').then((module) => ({ default: module.PendingSiteAccess })));
// const AsyncUserRoleManager = asyncComponent(() => import('../modules/cbre-flow-user-role/index').then((module) => ({ default: module.UserRoleManager })));
const AsyncAdminUser = asyncComponent(() => import('../modules/cbre-flow-admin-user-role/index').then((module) => ({ default: module.AdminUserRole })));


const RestrictedRoute = ({ component: Component, isSystemAdmin, authUser, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
    { 
      if(rest.hasCurrentViewAccess === false && rest.hasRequestedSiteAccess && !isSystemAdmin) {
        return <Redirect to="/elis/site-pending-access" />;
      }
      return (authUser || isSystemAdmin) ? (
        <Component {...props} />
      ) : (
        <AccessDenied />
      )}
    }
  />
);

const RestrictedAdminRoute = ({ component: Component, isSystemAdmin, authUser, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
    { 
      if(rest.hasCurrentViewAccess === false && rest.hasRequestedSiteAccess && !isSystemAdmin) {
        return <Redirect to="/elis/site-pending-access" />;
      }
      return authUser && isSystemAdmin ? (
        <Component {...props} />
      ) : (
        <AccessDenied />
      )}
    }
  />
);
// eslint-disable-next-line react/prop-types
const Routes = ({ match }) => {

  const { hasInstrumentPageAccess, hasWorkRequestPageAccess, hasEntitlementReviewerWindowAccess, isSystemAdmin, hasEntitlementReviewWindowAccess, isSiteForApproval, hasSiteAccess, hasApprovalPageAccess, hasUserAndRolePageAccess } = useSelector(getUserAccess)

  return (
    <Switch>
      <Route
        path={`${match.url}dashboard`}
        render={() => <Redirect to={`${match.url}elis/dashboard`} />}
      />
      <Route
        path={`${match.url}elis/site-pending-access`}
        component={PendingSiteAccess}
      />
      <RestrictedRoute
        path={`${match.url}elis/instruments`}
        authUser={hasInstrumentPageAccess || isSystemAdmin}
        hasSiteAccess={hasSiteAccess}
        isSystemAdmin={isSystemAdmin}
        exact
        component={AsyncLabInstrument}
      />
      <RestrictedRoute
        path={`${match.url}elis/instruments/details/:assetId`}
        authUser={hasInstrumentPageAccess || isSystemAdmin}
        hasSiteAccess={hasSiteAccess}
        isSystemAdmin={isSystemAdmin}
        exact
        component={AsyncLabInstrumentDetails}
      />
      <RestrictedRoute
        path={`${match.url}elis/requests`}
        authUser={hasWorkRequestPageAccess || isSystemAdmin}
        hasSiteAccess={hasSiteAccess}
        isSystemAdmin={isSystemAdmin}
        exact
        component={AsyncRequests}
      />
      <RestrictedRoute
        path={`${match.url}elis/requests/details/:requestId`}
        authUser={hasWorkRequestPageAccess || isSystemAdmin}
          
        isSystemAdmin={isSystemAdmin}
        exact
        component={AsyncRequestDetails}
      />
      <RestrictedRoute
        path={`${match.url}elis/dashboard`}
        authUser={true}
        isSystemAdmin={isSystemAdmin}
        component={AsyncResizableDashboard}
      />
      <Route
        path={`${match.url}elis/location`}
        authUser={true}
        isSystemAdmin={isSystemAdmin}
        component={AsyncSiteSelection}
      />
      <RestrictedAdminRoute
        path={`${match.url}elis/tableau-reports`}
        authUser={isSystemAdmin}
        isSystemAdmin={isSystemAdmin}
        component={AsyncTableauReports}
      />
      <RestrictedAdminRoute
        path={`${match.url}elis/pdf-generation`}
        authUser={isSystemAdmin}
        isSystemAdmin={isSystemAdmin}
        component={AsyncPDFGeneration}
      />
      <RestrictedRoute
        path={`${match.url}elis/windows/entitlements-review/details/:windowId`}
        authUser={hasEntitlementReviewWindowAccess }
        isSystemAdmin={isSystemAdmin}
        exact
        component={AsyncReviewWindowDetails}
      />
      <RestrictedRoute
        path={`${match.url}elis/approvals`}
        authUser={hasApprovalPageAccess || isSystemAdmin}
        isSystemAdmin={isSystemAdmin}
        component={AsyncApprovalsContainer}
      />
      <RestrictedRoute
        path={`${match.url}elis/windows/entitlements-review`}
          authUser={hasEntitlementReviewWindowAccess}
        exact
        isSystemAdmin={isSystemAdmin}
        component={AsyncRootEntitlementsReview}
      />
      <RestrictedRoute
        path={`${match.url}elis/windows/entitlements-reviewer/details/:windowId`}
        authUser={hasEntitlementReviewerWindowAccess}
        exact
        isSystemAdmin={isSystemAdmin}
        component={AsyncReviewerWindowDetails}
      />
      <RestrictedRoute
        path={`${match.url}elis/windows/entitlements-reviewer-window`}
        authUser={hasEntitlementReviewerWindowAccess}
        exact
        isSystemAdmin={isSystemAdmin}
        component={AsyncRootEntitlementsReviewerWindow}
      />
      {/* <RestrictedRoute
        path={`${match.url}elis`}
        authUser={true}
        isSystemAdmin={isSystemAdmin}
        component={AsyncRootLabDashboard}
      /> */}
      <RestrictedAdminRoute
        path={`${match.url}administration/user-management`}
        authUser={isSystemAdmin}
        isSystemAdmin={isSystemAdmin}
        component={AsyncUserRequests}
      />
      <RestrictedRoute
        path={`${match.url}admin/users`}
        authUser={hasUserAndRolePageAccess || isSystemAdmin}
        isSystemAdmin={isSystemAdmin}
        component={AsyncAdminUser}
      />
      {/* <Route component={asyncComponent(() => import("app/routes/extraPages/routes/404"))}/> */}
      <Route exact path={[`${match.url}elis`, `${match.url}elis/`]}>
        <Redirect to={`${match.url}elis/dashboard`} />
      </Route>
    </Switch>

)};
export default withRouter(Routes);
